import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';

import { has, includes, isEmpty, isString, merge, pickBy, snakeCase, isNaN, uniq } from 'lodash';
import Logger from '@sm360/logger-sentry';
import PropTypes from 'prop-types';
import React from 'react';

import {
    ConfigsService as GeneralConfigService,
    ContentZonesService,
    LocaleService,
    UrlService,
    WindowProxy,
} from '@sm360/widget-commons-core-services';
import {
    AccountService,
    EVENTS,
    HistoryService,
    LocalStorageService,
    SessionStorageService,
    ReduxClient,
    ApolloClient,
    AprService,
} from '@sm360/widget-commons-general';

import { PurchaseMethods, Vehicle } from '@sm360/widget-commons-vehicle';
import TradeIn from '@sm360/widget-trade-in';

import * as ListingUsedActions from 'inventory-listing/actions/listingUsedActions.js';
import * as ListingUsedService from 'inventory-listing/services/ListingUsedService/ListingUsedService.js';
import { COLANDER_GROUPS, COLANDER_SLUGS } from 'inventory-listing/constants/colanders.js';

import ListingUsedModel from 'inventory-listing/models/ListingUsedModel.js';
import VehicleCriteriaModel from 'inventory-listing/models/VehicleCriteriaModel.js';

import ListingUsed from 'inventory-listing/components/ListingUsed/ListingUsed.jsx';

import en from 'inventory-listing/i18n/en.json';
import fr from 'inventory-listing/i18n/fr.json';
import listingUsedReducer from 'inventory-listing/reducers/listingUsedReducer.js';
import vehiclesReducer from 'inventory-listing/reducers/vehiclesReducer.js';
import { fetchMakePriorities } from 'inventory-listing/services/CatalogAPIService/CatalogAPIService';
import SORTINGS from 'inventory-listing/constants/sortings.js';
import BODY_STYLES from 'inventory-listing/constants/bodyStyle.js';

const { TradeInVehicleActions } = Vehicle;

const {
    PurchaseMethodsActions,
    PurchaseMethodsConstant,
    PurchaseMethodsModel,
    PurchaseMethodsConstantV25,
} = PurchaseMethods;

const snakeCaseOrDefault = (str, defaultValue) => (isString(str) && str !== '' ? snakeCase(str) : defaultValue);

class ListingUsedProvider extends React.Component {
    constructor(props) {
        super(props);

        merge(en, Vehicle.Translation.en, PurchaseMethods.Translations.en, TradeIn.Translations.en);
        merge(fr, Vehicle.Translation.fr, PurchaseMethods.Translations.fr, TradeIn.Translations.fr);

        LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });

        GeneralConfigService.setOrganizationId(props.organizationId);
        GeneralConfigService.setOrganizationUnitId(props.organizationUnitId);
        GeneralConfigService.setOrganizationUnitLocation(props.organizationUnitLocation);
        GeneralConfigService.setWebsiteId(props.websiteId);
        GeneralConfigService.setLocation(props.location);

        this.logger = new Logger(this.constructor.displayName, GeneralConfigService.getLoggerContext());
        if (props.configs && props.configs.source && props.configs.source.image) {
            ContentZonesService.setImageServerBaseUrl(props.configs.source.image);
        }
        ContentZonesService.addContentZones(props.contentZones);

        this.storeRedux = ReduxClient.getOrCreate();
        ReduxClient.addReducers({
            listingUsed: listingUsedReducer,
            vehicles: vehiclesReducer,
            tradeInVehicle: Vehicle.TradeInVehicleReducer,
        });

        this.formatProperties = this.formatProperties.bind(this);
        this.getDefaultColanders = this.getDefaultColanders.bind(this);
        this.getColanderList = this.getColanderList.bind(this);
        this.buildVehicleCriteria = this.buildVehicleCriteria.bind(this);
        this.buildModel = this.buildModel.bind(this);
        this.onTradeInCreated = this.onTradeInCreated.bind(this);
        this.checkTradeInVehicleStatus = this.checkTradeInVehicleStatus.bind(this);
        this.onUnload = this.onUnload.bind(this);
    }

    async componentDidMount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.addEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            $window.addEventListener('beforeunload', this.onUnload);
        }
        const preferredPurchaseMethod = UrlService.getUrlParameter('preferredPurchaseMethod');

        const props = this.formatProperties(this.props);

        const model = await this.buildModel(props);
        if (model) {
            this.storeRedux.dispatch(ListingUsedActions.setListingUsed(model));
        }

        this.storeRedux.dispatch(ListingUsedActions.translateListingUsed());

        if (includes(PurchaseMethodsConstant, preferredPurchaseMethod)) {
            this.storeRedux.dispatch(ListingUsedActions.selectPurchaseMethod(preferredPurchaseMethod));
        }
        SessionStorageService.set('dealerMake', this.props?.dealerMake || '');
        this.checkTradeInVehicleStatus(true);
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.removeEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            this.onUnload();
        }
    }

    onUnload() {
        HistoryService.add(WindowProxy.location().href);
    }

    onTradeInCreated() {
        this.checkTradeInVehicleStatus(true);
    }

    getDefaultColanders(props) {
        const { colanders } = props;
        if (!colanders || !colanders.widgetColanderGroups) {
            this.logger.error(`The colanders are invalid [${colanders}]`);
            return [];
        }

        const widgetColanderGroup = colanders.widgetColanderGroups.find(
            (colanderGroup) => snakeCase(colanderGroup.slug) === snakeCase(props.colanderGroupSlug)
        );

        if (widgetColanderGroup) {
            return widgetColanderGroup.colanders;
        }

        this.logger.error(`There is no widgetColanderGroup for the slug [${props.colanderGroupSlug}]`);
        return [];
    }

    getColanderList(props) {
        const { colanders } = props;
        if (!colanders || !colanders.widgetColanderGroups) {
            this.logger.error(`The colanders are invalid [${colanders}]`);
            return [];
        }

        const widgetColanderGroup = colanders.widgetColanderGroups.find(
            (colanderGroup) => snakeCase(colanderGroup.slug) === snakeCase(props.colanderGroupSlug)
        );

        if (widgetColanderGroup) {
            return colanders.widgetColanderGroups;
        }

        this.logger.error(`There is no colanderList for the slug [${props.colanderGroupSlug}]`);
        return [];
    }

    mapFrameStyleTypesStrToId(selectedFramestyles) {
        const UpdatedParamValue = selectedFramestyles.reduce((acc, style) => {
            BODY_STYLES.forEach((bodyStyle) => {
                if (
                    bodyStyle?.group.toLowerCase() === style.replace(/_/g, '').toLowerCase() &&
                    typeof bodyStyle?.codes[0] !== 'string'
                ) {
                    acc.push(bodyStyle.codes.toString());
                }
            });
            return acc;
        }, []);
        return UpdatedParamValue;
    }

    // framestyleTypes must be integer
    // For specific case we are getting framestyleTypes as string.
    /**
     * framestyleTypes params must be receive Ids (integer)
     * For specific case we are getting framestyleTypes as string.
     * This function would convert string types to ids by mapping with bodystyles constant
     */
    checkFrameStyleTypesParams() {
        if (UrlService.getUrlParameter('frameStyleTypes')) {
            const frameStyles = UrlService.getUrlParameter('frameStyleTypes').split(',');

            // Replace the URL params only if type is string
            const strFrameStyleTypes = [];
            const intFrameStyleTypes = [];
            if (frameStyles?.length > 0) {
                frameStyles.forEach((frameStyle) => {
                    if (isNaN(parseFloat(frameStyle) && isNaN(frameStyle - 0))) {
                        // Found string frameStylesTypes
                        strFrameStyleTypes.push(frameStyle);
                    } else {
                        // Found int frameStylesTypes
                        intFrameStyleTypes.push(frameStyle);
                    }
                });

                let allFrameStyleValue;
                const convertedFrameStyleValue =
                    strFrameStyleTypes?.length > 0 && this.mapFrameStyleTypesStrToId(strFrameStyleTypes);
                if (convertedFrameStyleValue?.length > 0 && intFrameStyleTypes?.length > 0) {
                    allFrameStyleValue = convertedFrameStyleValue.concat(intFrameStyleTypes);
                } else {
                    allFrameStyleValue = convertedFrameStyleValue?.length > 0 ? convertedFrameStyleValue : [];
                }

                if (strFrameStyleTypes?.length > 0) {
                    UrlService.addOrReplaceParameter('frameStyleTypes', uniq(allFrameStyleValue));
                }
            }
        }
    }

    checkTradeInVehicleStatus(forceLoad) {
        const state = this.storeRedux.getState();
        if (forceLoad || isEmpty(state.tradeInVehicle) || !state.tradeInVehicle.id) {
            if (AccountService.isAuthenticated()) {
                this.storeRedux.dispatch(TradeInVehicleActions.loadTradeInVehicle(state.tradeInVehicle));
            } else {
                const defaultPurchaseMethods = new PurchaseMethodsModel();
                this.storeRedux.dispatch(PurchaseMethodsActions.setTradeIn(defaultPurchaseMethods.tradeIn));
                this.storeRedux.dispatch(PurchaseMethodsActions.setLien(defaultPurchaseMethods.lien));
            }
        }
    }

    formatProperties(props) {
        const properties = { ...props };
        const { colanderGroupSlug, colanders } = properties;

        if (colanders && colanders.currentWidgetColanderGroup) {
            properties.colanders.currentWidgetColanderGroup.slug = snakeCaseOrDefault(
                colanders.currentWidgetColanderGroup.slug,
                this.constructor.defaultProps.colanderGroupSlug
            );
        }

        if (colanders && colanders.widgetColanderGroups) {
            properties.colanders.widgetColanderGroups = colanders.widgetColanderGroups.map((item) => {
                item.slug = snakeCaseOrDefault(item.slug, this.constructor.defaultProps.colanderGroupSlug);
                return item;
            });
        }

        properties.colanderGroupSlug = snakeCaseOrDefault(
            colanderGroupSlug,
            this.constructor.defaultProps.colanderGroupSlug
        );

        return properties;
    }

    formatUrl(url) {
        return url?.toLowerCase().replace(/ /g, '_');
    }

    buildVehicleCriteria(props) {
        this.checkFrameStyleTypesParams();
        const context = props.listingContext;
        const contextMakeId = UrlService.containsPath(snakeCase(context?.make?.name)) && context?.make?.id;
        const contextModelId = UrlService.containsPath(this.formatUrl(context?.model?.name)) && context?.model?.id;
        const contextYear = UrlService.containsPath(context?.year) && context?.year;

        const drivetrains = UrlService.getUrlParameter('drivetrains');
        const makeIds = contextMakeId ? contextMakeId.toString() : UrlService.getUrlParameter('makeIds');
        const frameStyle = UrlService.getUrlParameter('frameStyleTypes');
        const fuelTypes = UrlService.getUrlParameter('fuelTypes');
        const transmissionTypes = UrlService.getUrlParameter('transmissionTypes');
        const odometerFrom = UrlService.getUrlParameter('odometerFrom');
        const odometerTo = UrlService.getUrlParameter('odometerTo');
        const textQuerySearch = UrlService.getUrlParameter('textQuery');
        const salePriceRangeFrom = UrlService.getUrlParameter('salePriceFrom');
        const salePriceRangeTo = UrlService.getUrlParameter('salePriceTo');
        const yearFrom = contextYear || UrlService.getFirstUrlParameter(['yearFrom', 'minYear']);
        const yearTo = contextYear || UrlService.getFirstUrlParameter(['yearTo', 'maxYear']);

        // check if there are catalog make or model ids only if the filters are not being used
        const catalogMakeId = !context?.make?.id && !makeIds ? UrlService.getUrlParameter('catalogMakeId') : '';
        const catalogModelId = !context?.model?.id ? UrlService.getUrlParameter('catalogModelId') : '';

        // Here 'colanderSlug' represent the slug of the colander from the context
        const colanderSlug = context && isString(context.colanderSlug) ? context.colanderSlug : COLANDER_SLUGS.USED;

        const onlyBuiltVehicles = isString(props.colanderGroupSlug)
            ? props.colanderGroupSlug === COLANDER_GROUPS.NEW
            : true;

        const yearFromNumber = parseInt(yearFrom, 10);
        const yearToNumber = parseInt(yearTo, 10);
        const yearRange =
            Number.isFinite(yearFromNumber) && Number.isFinite(yearToNumber)
                ? {
                      from: yearFromNumber,
                      to: yearToNumber,
                      type: 'CLOSED',
                  }
                : {};

        const odometerFromNumber = parseInt(odometerFrom, 10);
        const odometerToNumber = parseInt(odometerTo, 10);
        const odometerRange = Number.isFinite(odometerToNumber)
            ? {
                  type: 'CLOSED',
                  from: Number.isFinite(odometerFromNumber) ? odometerFromNumber : 0,
                  to: odometerToNumber,
              }
            : {};

        const priceFromNumber = parseInt(salePriceRangeFrom, 10);
        const priceToNumber = parseInt(salePriceRangeTo, 10);
        const salePriceRange = Number.isFinite(priceToNumber)
            ? {
                  type: 'CLOSED',
                  from: Number.isFinite(priceFromNumber) ? priceFromNumber : 0,
                  to: priceToNumber,
              }
            : {};

        let textQuery = String(textQuerySearch);
        if (textQuery && isString(textQuery)) textQuery = textQuery.replaceAll('+', ' ');

        const vehicleCriteria = new VehicleCriteriaModel({
            vehicleStatuses: ['FOR_SALE', 'SOLD', 'VIRTUAL', 'ON_HOLD'],
            colanderSlug,
            onlyBuiltVehicles,
            drivetrains: drivetrains ? drivetrains.split(',') : [],
            makeIds: makeIds ? makeIds.split(',') : [],
            fuelTypes: fuelTypes ? fuelTypes.split(',') : [],
            frameStyleTypes: frameStyle ? frameStyle.split(',') : [],
            modelIds: contextModelId ? [contextModelId] : [],
            transmissionTypes: transmissionTypes ? transmissionTypes.split(',') : [],
            odometerRange,
            yearRange,
            salePriceRange,
            textQuery,
            catalogMakeIds: catalogMakeId ? [catalogMakeId] : [],
            catalogModelIds: catalogModelId ? [catalogModelId] : [],
        });

        return vehicleCriteria;
    }

    buildModel(props) {
        const colanders = this.getDefaultColanders(props);
        const colanderList = this.getColanderList(props);
        const vehicleCriteria = this.buildVehicleCriteria(props);
        const urlParams = UrlService.getUrlParameters() || {};

        const localStorageCashDown = parseInt(LocalStorageService.get('cashDown'), 10) || null;
        const initialCashDown =
            has(urlParams, 'initialCashDown') && parseInt(urlParams.initialCashDown, 10)
                ? parseInt(urlParams.initialCashDown, 10)
                : localStorageCashDown;

        const initialTargetPayment =
            has(urlParams, 'initialTargetPayment') && parseInt(urlParams.initialTargetPayment, 10)
                ? parseInt(urlParams.initialTargetPayment, 10)
                : null;

        const maximumTerm =
            has(urlParams, 'maximumTerm') && parseInt(urlParams.maximumTerm, 10)
                ? parseInt(urlParams.maximumTerm, 10)
                : null;

        const preferredPaymentFrequency =
            has(urlParams, 'preferredPaymentFrequency') && parseInt(urlParams.preferredPaymentFrequency, 10)
                ? parseInt(urlParams.preferredPaymentFrequency, 10)
                : null;

        const contentZoneDefaultPaymentType = ContentZonesService.getContentZone(
            'widget.listing.used',
            'default.purchase.method'
        );

        // Get purchase method from URL if not found use content zone default payment type
        const preferredPurchaseMethod = UrlService.getUrlParameter('preferredPurchaseMethod');
        let defaultPurchaseMethodCZ = contentZoneDefaultPaymentType && contentZoneDefaultPaymentType.getValue();

        // Check for only Listing Used
        if (this.props.colanderGroupSlug === COLANDER_GROUPS.DEFAULT) {
            const contentZoneUsedListingDefaultPaymentType = ContentZonesService.getContentZone(
                'widget.listing.used',
                'default.purchase.method.used.only'
            );

            const listingUsedPurchaseMethodCZ = contentZoneUsedListingDefaultPaymentType?.getValue() || '';

            /**
             * Consider CZ value if its either 'finance' or 'cash'
             * for any other value (lease) in CZ it will not consider this CZ and use the value from 'default.purchase.method' CZ
             */
            if (
                listingUsedPurchaseMethodCZ &&
                [
                    PurchaseMethodsConstantV25.FINANCE,
                    PurchaseMethodsConstantV25.CASH,
                    PurchaseMethodsConstantV25.CASH_PURCHASE,
                ].includes(listingUsedPurchaseMethodCZ)
            ) {
                defaultPurchaseMethodCZ = listingUsedPurchaseMethodCZ;
            }

            // if CZ for listing used is not set and CZ value for generic CZ (default.purchase.method) is lease
            if (!listingUsedPurchaseMethodCZ && defaultPurchaseMethodCZ === PurchaseMethodsConstantV25.LEASE) {
                defaultPurchaseMethodCZ = PurchaseMethodsConstantV25.CASH;
            }
        }

        if (defaultPurchaseMethodCZ === PurchaseMethodsConstantV25.CASH_PURCHASE)
            defaultPurchaseMethodCZ = PurchaseMethodsConstantV25.CASH;

        const purchaseMethod = preferredPurchaseMethod || defaultPurchaseMethodCZ;

        const paymentOptions = new Vehicle.PaymentOptionsModel(
            pickBy({
                purchaseMethod,
                initialCashDown,
                initialTargetPayment,
                maximumTerm,
                preferredPaymentFrequency,
            })
        );

        // Default sorting
        const contentZoneDefaultSort = ContentZonesService.getContentZone('widget.listing.used', 'default.sort');
        let multipleDefaultSort =
            contentZoneDefaultSort && contentZoneDefaultSort.getValue() && contentZoneDefaultSort.getValue().split(',');
        multipleDefaultSort =
            multipleDefaultSort && multipleDefaultSort.length > 0 ? multipleDefaultSort : ['SALE_PRICE_ASC'];
        multipleDefaultSort = multipleDefaultSort.map((sorting) => ListingUsedService.getSortObjectFromField(sorting));

        // Vehicles order saved in memory
        let vehicleOrders = {};

        if (SessionStorageService.get('vehicleSorting')) {
            const vehicleSort = JSON.parse(SessionStorageService.get('vehicleSorting'));
            if (vehicleSort !== SORTINGS.DEFAULT.value) {
                vehicleOrders = ListingUsedService.getSortObjectFromField(vehicleSort);
            }
        }

        const listing = {
            colanderGroup: props.colanderGroupSlug,
            colanderCategorySlug: props.colanderCategorySlug || '',
            colanderList,
            colanders,
            dealerMake: props.dealerMake,
            vehicleCriteria,
            vehicleOrders,
            defaultVehicleOrders: multipleDefaultSort,
            contextUrl: props.urlTemplate,
            carfaxKey: props.carproofKey,
            vdpUrl: props.vdpUrl,
            paymentOptions,
            motoInsightNewEnabled: props.motoInsightNewEnabled,
            motoInsightUsedEnabled: props.motoInsightUsedEnabled,
            roadsterNewEnabled: props.roadsterNewEnabled,
            roadsterUsedEnabled: props.roadsterUsedEnabled,
            eshopNewEnabled: props.eshopNewEnabled,
            eshopUsedEnabled: props.eshopUsedEnabled,
            shopClickDriveNewEnabled: props.shopClickDriveNewEnabled,
            shopClickDriveUsedEnabled: props.shopClickDriveUsedEnabled,
            roadsterLink: props.roadsterLink,
            dealerCode: props.dealerCode,
            dealerBAC: props.dealerBAC,
        };

        // Here we use 'pickBy' to filter truthy values.
        // Falsy values are set inside the model to defaults.
        const listingUsedModel = new ListingUsedModel(pickBy(listing));

        return fetchMakePriorities(props.organizationUnitId)
            .catch(() => {
                this.logger.error(`Unable to fetch make priorities while building listing`);
                return [];
            })
            .then((makePriorities) => {
                return listingUsedModel.set('makePriorities', makePriorities);
            });
    }

    render() {
        const props = this.formatProperties(this.props);
        const orgUnitId = GeneralConfigService?.getOrganizationUnitId();
        const location = GeneralConfigService?.getOrganizationUnitLocation();
        const displayCreditRate = AprService.displayApr(location, orgUnitId);

        return (
            <div>
                <ApolloProvider client={props.apolloClient}>
                    <Provider store={this.storeRedux}>
                        <ListingUsed {...props} displayCreditRate={displayCreditRate} />
                    </Provider>
                </ApolloProvider>
            </div>
        );
    }
}

ListingUsedProvider.defaultProps = {
    dealerMake: '',
    listingContext: {},
    colanderGroupSlug: COLANDER_GROUPS.DEFAULT,
    colanderCategorySlug: '',
    listingH1: LocaleService.t('FIND_USED_CAR'),
    carproofKey: '',
    apolloClient: ApolloClient.getOrCreate(),
    configs: null,
    hasMakePriorityEnabled: false,
    websiteSoldDaysShown: 0,
    showCarfax: true, // Default set to true if props is not available, otherwise flag is available in XMS website management
    isCustomColanderSlug: false, // TODO: remove this props after Humberview Demo
    motoInsightNewEnabled: false,
    motoInsightUsedEnabled: false,
    roadsterNewEnabled: false,
    roadsterUsedEnabled: false,
    eshopNewEnabled: false,
    eshopUsedEnabled: false,
    shopClickDriveNewEnabled: false,
    shopClickDriveUsedEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
};

ListingUsedProvider.propTypes = {
    locale: PropTypes.string.isRequired,
    websiteId: PropTypes.number.isRequired,
    location: PropTypes.string.isRequired,
    configs: PropTypes.shape({
        source: PropTypes.shape({
            image: PropTypes.string,
        }),
    }),
    organizationUnitLocation: PropTypes.string.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationId: PropTypes.number.isRequired,
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
    listingContext: PropTypes.shape({
        // eslint-disable-line react/no-unused-prop-types
        make: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        }),
        model: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        }),
        category: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            description: PropTypes.string,
        }),
        colanderSlug: PropTypes.string,
    }),
    listingH1: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    urlTemplate: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    vdpUrl: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    colanders: PropTypes.shape({
        // eslint-disable-line react/no-unused-prop-types
        widgetColanderGroups: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                slug: PropTypes.string,
            })
        ),
    }).isRequired,
    colanderGroupSlug: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    websiteSoldDaysShown: PropTypes.number,
    showCarfax: PropTypes.bool.isRequired,
    motoInsightNewEnabled: PropTypes.bool,
    motoInsightUsedEnabled: PropTypes.bool,
    roadsterNewEnabled: PropTypes.bool,
    roadsterUsedEnabled: PropTypes.bool,
    eshopNewEnabled: PropTypes.bool,
    eshopUsedEnabled: PropTypes.bool,
    shopClickDriveNewEnabled: PropTypes.bool,
    shopClickDriveUsedEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
};

ListingUsedProvider.displayName = 'ListingUsedProvider';

export default ListingUsedProvider;
